import { Button, Group, Modal, TextInput } from '@mantine/core';
import { useState } from 'react';
import { useIntl } from 'react-intl';

interface RenameFileModalProps {
  opened: boolean;
  onClose: () => void;
  currentFileName: string;
  existingFileNames: string[];
  onRename: (newName: string) => void;
}

export const RenameFileModal = ({
  opened,
  onClose,
  currentFileName,
  existingFileNames,
  onRename,
}: RenameFileModalProps) => {
  const intl = useIntl();
  const [newFileName, setNewFileName] = useState(currentFileName);
  const [error, setError] = useState<string>('');

  const handleClose = () => {
    setNewFileName(currentFileName);
    setError('');
    onClose();
  };

  const checkDuplicateName = (name: string): boolean => {
    const trimmedName = name.trim().toLowerCase();
    return existingFileNames.some(
      (existingName) => existingName.toLowerCase() === trimmedName
    );
  };

  const confirmRename = () => {
    const trimmedName = newFileName.trim();

    // Validation
    if (!trimmedName) {
      setError(intl.formatMessage({ id: 'fileNameRequired' }));
      return;
    }

    if (trimmedName === currentFileName) {
      handleClose();
      return;
    }

    // Check for duplicate name
    if (checkDuplicateName(trimmedName)) {
      setError(intl.formatMessage({ id: 'fileNameAlreadyExists' }));
      return;
    }

    // All validations passed
    onRename(trimmedName);
    handleClose();
  };

  return (
    <Modal
      opened={opened}
      onClose={handleClose}
      title={intl.formatMessage({ id: 'rename' })}
      centered
    >
      <TextInput
        label={intl.formatMessage({ id: 'newName' })}
        value={newFileName}
        onChange={(event) => {
          setNewFileName(event.currentTarget.value);
          setError(''); // Clear error on change
        }}
        error={error}
        autoFocus
        onKeyDown={(event) => {
          if (event.key === 'Enter') {
            confirmRename();
          }
        }}
      />
      <Group justify="flex-end" mt="md">
        <Button variant="subtle" onClick={handleClose}>
          {intl.formatMessage({ id: 'cancel' })}
        </Button>
        <Button onClick={confirmRename}>
          {intl.formatMessage({ id: 'save' })}
        </Button>
      </Group>
    </Modal>
  );
};
